import { useRef, useEffect } from 'react'
import { Bold, Italic, Underline, Link2, List, ListOrdered, Heading, RemoveFormatting } from 'lucide-react'

/**
 * A small dependency-free rich-text editor: a formatting toolbar over a contenteditable
 * surface. Emits HTML via onChange so it can drop straight into an email's html body.
 * Uses execCommand — deprecated but universally supported and perfect for a compose box.
 */
export function RichText({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)

  // Seed the editable surface once. Re-setting innerHTML on every keystroke would move the caret.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function emit() { if (ref.current) onChange(ref.current.innerHTML) }
  function cmd(command: string, arg?: string) {
    ref.current?.focus()
    document.execCommand(command, false, arg)
    emit()
  }
  function link() {
    const url = prompt('Link URL', 'https://')
    if (url) cmd('createLink', url)
  }

  const btn = { type: 'button' as const }
  return (
    <div className="rte">
      <div className="rte-tools">
        <button {...btn} onClick={() => cmd('bold')} title="Bold"><Bold size={15} /></button>
        <button {...btn} onClick={() => cmd('italic')} title="Italic"><Italic size={15} /></button>
        <button {...btn} onClick={() => cmd('underline')} title="Underline"><Underline size={15} /></button>
        <span className="rte-sep" />
        <button {...btn} onClick={() => cmd('formatBlock', 'H2')} title="Heading"><Heading size={15} /></button>
        <button {...btn} onClick={() => cmd('insertUnorderedList')} title="Bulleted list"><List size={15} /></button>
        <button {...btn} onClick={() => cmd('insertOrderedList')} title="Numbered list"><ListOrdered size={15} /></button>
        <button {...btn} onClick={link} title="Insert link"><Link2 size={15} /></button>
        <span className="rte-sep" />
        <button {...btn} onClick={() => cmd('removeFormat')} title="Clear formatting"><RemoveFormatting size={15} /></button>
      </div>
      <div ref={ref} className="rte-body" contentEditable suppressContentEditableWarning
           onInput={emit} onBlur={emit} data-placeholder="Write your email…" />
    </div>
  )
}
